import React, { useCallback, useMemo } from 'react';
import { Quotation, SalesOrder } from '../../graphql/entities';
import attributeQuery from '../../graphql/queries/sales/SalesAttributeConfigListQuery.graphql';
import customerAttributeQuery from 'graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import Formatters from '../../components/grid-addons/formatters';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import { CostViewPermission, Direction, ISalesOrder } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { useSelector } from 'react-redux';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IReduxStore } from '../../@types/reduxTypes';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const LazyCustomerForm: any = React.lazy(() => import('../customer/CustomerForm'));

const QUOTE_COLUMN_OVERRIDES = {
  orderNumber: {
    name: 'Quote No',
    key: 'orderNumber',
    width: 100,
  },
  orderDate: {
    name: 'Order Date',
    key: 'orderDate',
    formatter: 'date',
    width: 120,
  },
  'customer.name': {
    name: 'Customer Name',
  },
  'customer.reference': {
    name: 'Customer Reference',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyCustomerForm,
      modalProps: { forceReadOnly: true },
      entityId: 'customer.id',
    },
  },
  goodsTotal: {
    name: 'Goods Total',
    cellClass: 'rightAlign',
  },
  salesOrderStatus: {
    name: 'Status',
    sortable: false,
  },
  targetDate: {
    name: 'Expected Delivery Date',
  },
  comment: {
    formatter: Formatters.CommentFormatter,
  },
};

const QUOTE_FIELD_NAMES: Array<Paths<ISalesOrder>> = [
  'orderNumber',
  'orderDate',
  'customer.reference',
  'customer.name',
  'orderReference',
  'quoteStatus',
  'headerTotal',
];

export const BaseQuoteWorkspace = (props) => {
  const { entity, columnOverrides = [], fieldNames = [], predefinedFilters = [] } = props;
  const { formUrl, type, title, workspace } = entity;

  const attributeColumns = useAttributesForWorkspace(attributeQuery);
  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(
    () => ({ ...(props.columnColumns || {}), ...attributeColumns, ...customerAttributeColumns }),
    [props.customColumns, attributeColumns, customerAttributeColumns]
  );

  const workspaceProps = useWorkspace<ISalesOrder>(
    { ...props, predefinedFilters: [...predefinedFilters, ...SalesOrder.quoteWorkspacePredefinedFilters] },
    entity,
    {
      columns: fieldNames,
      customColumns: customColumns,
      initialSortColumn: 'orderNumber',
      initialSortDirection: Direction.Desc,
    }
  );

  const costViewPermission = useSelector(
    (store: IReduxStore) => store.app?.fullUserDetails?.companyUser?.costViewPermission
  );

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }
      return !(
        costViewPermission !== CostViewPermission.All &&
        (column.fieldName === 'baseCostTotal' ||
          column.fieldName === 'costTotal' ||
          column.fieldName === 'baseProfitValue' ||
          column.fieldName === 'profitValue' ||
          column.fieldName === 'profitMargin')
      );
    },
    [costViewPermission]
  );

  return (
    <Workspace
      ref={props.setWorkspaceRef}
      entityType={type}
      title={props.title || title}
      formUrl={formUrl}
      defaultQuery={workspace}
      duplicate={true}
      viewScreenName={props.viewScreenName}
      availableFiltersFilter={props.availableFiltersFilter}
      entity={entity}
      availableColumnsFilter={availableColumnsFilter}
      {...workspaceProps}
      {...props}
      columnOverrides={columnOverrides}
    />
  );
};

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  return column?.fieldName && !column.fieldName.includes('orderType');
};

export default (props) => {
  const passedFields = props.fieldNames ? props.fieldNames : [];

  const columnOverrides = useMemo(() => {
    const passedOverrides = props.columnOverrides ? props.columnOverrides : [];
    return {
      ...QUOTE_COLUMN_OVERRIDES,
      ...passedOverrides,
    };
  }, [props.columnOverrides]);

  const fieldNames: Array<Paths<ISalesOrder>> = [...QUOTE_FIELD_NAMES, ...passedFields];

  return (
    <BaseQuoteWorkspace
      entity={Quotation}
      viewScreenName="Sales/Quote/Workspace"
      availableFiltersFilter={availableFiltersFilter}
      {...props}
      fieldNames={fieldNames}
      columnOverrides={columnOverrides}
    />
  );
};
